import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Button from 'components/Basic/Button';
import { getShortcutSections } from './shortcuts';
import {
	Body, CloseBtn, Header, KeyCap, Keys, Label, Row, Rows, Section, SectionTitle, ShortcutsPopup, Title,
} from './style';

// no keyboard on phones/tablets; a narrow desktop window keeps the button.
// read on every render (not once at load) so the DevTools device toolbar updates it live
const isIOSOrAndroid = () => /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) ||
	(navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 0);
const MODIFIER_KEYS = ['Shift', 'Control', 'Meta', 'Alt'];

export default function KeyboardShortcutsBtn() {
	const [open, setOpen] = useState(false);
	const { designType, mode } = useSelector((state: RootState) => state.roofData.data);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);
	const edgeSetbacks = useSelector((state: RootState) => state.designSettigs.data.useEdgeTypeBasedSetbacks);
	const hidden = isIOSOrAndroid();

	// open: block keys behind the dialog, Esc (on keyup) closes, modifier keyups pass so none get stuck
	useEffect(() => {
		if (!open || hidden) return;
		const handleKey = (event: KeyboardEvent) => {
			if (event.type === 'keyup' && MODIFIER_KEYS.includes(event.key)) return;
			event.stopPropagation();
			if (event.type === 'keyup' && event.key === 'Escape') setOpen(false);
		};
		window.addEventListener('keydown', handleKey, { capture: true });
		window.addEventListener('keyup', handleKey, { capture: true });
		return () => {
			window.removeEventListener('keydown', handleKey, { capture: true });
			window.removeEventListener('keyup', handleKey, { capture: true });
		};
	}, [open, hidden]);

	if (hidden) return null;

	const sections = getShortcutSections({ designType, mode, allowedActions, edgeSetbacks });
	// an odd last narrow card goes full width (no hole beside it)
	const narrow = sections.filter(({ wide }) => !wide);
	const loneNarrow = narrow.length % 2 ? narrow[narrow.length - 1].title : undefined;

	return (
		<>
			<Button
				id="shortcuts-btn"
				className="shortcuts-btn btnHover"
				tooltipContent="Keyboard Shortcuts"
				aria-label="Keyboard shortcuts"
				aria-haspopup="dialog"
				onClick={() => setOpen(true)}
			>
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="1.4" y="3.9" width="17.2" height="12.2" rx="2.8" stroke="var(--defaultIconColor)" strokeWidth="1.6" />
					<path d="M5.2 8h.01M8.4 8h.01M11.6 8h.01M14.8 8h.01" stroke="var(--defaultIconColor)" strokeWidth="2" strokeLinecap="round" />
					<path d="M6 12h8" stroke="var(--defaultIconColor)" strokeWidth="1.6" strokeLinecap="round" />
				</svg>
			</Button>
			<ShortcutsPopup modal lockScroll open={open} onClose={() => setOpen(false)} closeOnEscape={false}>
				<Header>
					<Title>Keyboard Shortcuts</Title>
					<CloseBtn onClick={() => setOpen(false)} aria-label="Close" autoFocus>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1 1L13 13M13 1L1 13" stroke="var(--defaultIconColor)" strokeWidth="2" strokeLinecap="round" />
						</svg>
					</CloseBtn>
				</Header>
				<Body>
					{sections.map(({ title, hint, wide, shortcuts }) => {
						// wide cards: keys hug their label; narrow: shared key width so labels line up
						const isWide = wide || title === loneNarrow;
						// a 3-key combo (Ctrl + Shift + Z) outgrows the shared key column, widen it for the whole card
						const hasLongCombo = shortcuts.some(({ keys }) => keys.length > 2);
						return (
							<Section key={title} $wide={isWide}>
								<SectionTitle>{title}{hint && <small>{hint}</small>}</SectionTitle>
								<Rows>
									{shortcuts.map(({ keys, label }) => (
										<Row key={`${keys.join('+')} ${label}`}>
											<Keys $compact={isWide} $long={hasLongCombo}>
												{keys.map((key, index) => (
													<Fragment key={key}>
														{index > 0 && '+'}
														<KeyCap>{key}</KeyCap>
													</Fragment>
												))}
											</Keys>
											<Label>{label}</Label>
										</Row>
									))}
								</Rows>
							</Section>
						);
					})}
				</Body>
			</ShortcutsPopup>
		</>
	);
}
